import { Head, Link, router } from "@inertiajs/react";
import DashboardLayout from "../../../components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Calendar, Package, Percent, Eye, Edit, Trash2, Plus } from "lucide-react";

interface Promotion {
  id: number;
  name: string;
  description?: string;
  promotion_type: string;
  color: string;
  banner_text?: string;
  start_date: string;
  end_date: string;
  active: boolean;
  countdown_enabled: boolean;
  countdown_end?: string;
  products_count: number;
  average_discount: number;
  products: Array<{
    id: number;
    name: string;
    discount_percentage: number;
    active: boolean;
  }>;
}

interface IndexProps {
  promotions: Promotion[];
  store_logo?: string;
  store_name?: string;
}

const Index = ({ promotions, store_logo, store_name }: IndexProps) => {
  const getPromotionTypeLabel = (type: string) => {
    const types = {
      general: 'General',
      seasonal: 'Estacional',
      flash_sale: 'Oferta Flash',
      holiday: 'Días Festivos',
      special: 'Especial'
    };
    return types[type as keyof typeof types] || type;
  };

  const getPromotionTypeBadge = (type: string) => {
    const variants = {
      general: 'secondary',
      seasonal: 'default',
      flash_sale: 'destructive',
      holiday: 'outline',
      special: 'secondary'
    };
    return variants[type as keyof typeof variants] || 'secondary';
  };

  const isActive = (promotion: Promotion) => {
    const now = new Date();
    const startDate = new Date(promotion.start_date);
    const endDate = new Date(promotion.end_date);
    return promotion.active && now >= startDate && now <= endDate;
  };

  return (
    <DashboardLayout storeLogo={store_logo} storeName={store_name}>
      <Head title="Gestión de Promociones" />
      
      {/* Header */}
      <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/20 via-slate-400/20 to-slate-500/20 animate-pulse" />
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Promociones
            </h1>
            <p className="text-slate-50 text-lg">
              Gestiona las promociones y descuentos de tu tienda
            </p>
          </div>
          <Button asChild className="bg-white text-slate-700 hover:bg-slate-50 shadow-lg hover:shadow-xl transition-all duration-200 gap-2">
            <Link href="/admin/promotions/new">
              <Plus className="h-5 w-5" />
              Nueva Promoción
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-cyan-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 relative z-10">
            <CardTitle className="text-sm font-semibold text-slate-600 dark:text-slate-300">Total Promociones</CardTitle>
            <div className="p-2 rounded-lg bg-gradient-to-r from-slate-500 to-slate-600 shadow-sm">
              <Package className="h-5 w-5 text-white" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">{promotions.length}</div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-green-500/5 to-lime-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 relative z-10">
            <CardTitle className="text-sm font-semibold text-slate-600 dark:text-slate-300">Activas</CardTitle>
            <div className="p-2 rounded-lg bg-gradient-to-r from-slate-500 to-slate-600 shadow-sm">
              <Calendar className="h-5 w-5 text-white" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">{promotions.filter(isActive).length}</div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-violet-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 relative z-10">
            <CardTitle className="text-sm font-semibold text-slate-600 dark:text-slate-300">Productos en Promoción</CardTitle>
            <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-violet-500 shadow-sm">
              <Package className="h-5 w-5 text-white" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">{promotions.reduce((sum, p) => sum + p.products_count, 0)}</div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-yellow-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 relative z-10">
            <CardTitle className="text-sm font-semibold text-slate-600 dark:text-slate-300">Descuento Promedio</CardTitle>
            <div className="p-2 rounded-lg bg-gradient-to-r from-orange-500 to-yellow-500 shadow-sm">
              <Percent className="h-5 w-5 text-white" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              {promotions.length > 0
                ? (promotions.reduce((sum, p) => sum + Number(p.average_discount || 0), 0) / promotions.length).toFixed(1)
                : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promotions Table */}
      <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 shadow-lg">
              <Percent className="h-6 w-6 text-white" />
            </div>
            Lista de Promociones
          </CardTitle>
          <CardDescription className="text-base text-slate-600 dark:text-slate-400">
            Gestiona todas tus promociones desde aquí
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 hover:from-slate-200 hover:to-slate-300 dark:hover:from-slate-700 dark:hover:to-slate-600">
                   <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Promoción</TableHead>
                   <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Tipo</TableHead>
                   <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Color</TableHead>
                   <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Productos</TableHead>
                   <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Fechas</TableHead>
                   <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Estado</TableHead>
                   <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Activar/Desactivar</TableHead>
                   <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Descuento Prom.</TableHead>
                  <TableHead className="text-right font-semibold text-slate-700 dark:text-slate-300">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                 {promotions.length === 0 ? (
                   <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                       <div className="flex flex-col items-center gap-4">
                         <div className="p-4 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600">
                           <Package className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                         </div>
                         <p className="text-slate-500 dark:text-slate-400 font-medium">No hay promociones creadas</p>
                         <Button asChild variant="outline" className="hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 dark:hover:from-amber-900/20 dark:hover:to-orange-900/20 border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-300 hover:border-amber-400 dark:hover:border-amber-500 transition-all duration-200">
                           <Link href="/admin/promotions/new">Crear primera promoción</Link>
                         </Button>
                       </div>
                     </TableCell>
                   </TableRow>
                ) : (
                   promotions.map((promotion, index) => (
                     <TableRow key={promotion.id} className={`border-b border-slate-200 dark:border-slate-700 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 dark:hover:from-blue-900/10 dark:hover:to-indigo-900/10 transition-all duration-200 ${index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50/50 dark:bg-slate-800/50'}`}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{promotion.name}</div>
                          {promotion.banner_text && (
                            <div className="text-sm text-muted-foreground">{promotion.banner_text}</div>
                          )}
                        </div>
                      </TableCell>
                       <TableCell>
                         <Badge variant={getPromotionTypeBadge(promotion.promotion_type) as any}>
                           {getPromotionTypeLabel(promotion.promotion_type)}
                         </Badge>
                       </TableCell>
                       <TableCell>
                         <Badge variant="outline" className={`capitalize ${promotion.color === 'red' ? 'text-red-600' : promotion.color === 'green' ? 'text-green-600' : 'text-purple-600'}`}>
                           {promotion.color === 'red' ? 'Rojo' : promotion.color === 'green' ? 'Verde' : 'Morado'}
                         </Badge>
                       </TableCell>
                       <TableCell>
                         <div className="flex items-center gap-1">
                           <Package className="h-4 w-4 text-muted-foreground" />
                           <span>{promotion.products_count}</span>
                         </div>
                       </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{new Date(promotion.start_date).toLocaleDateString()}</div>
                          <div className="text-muted-foreground">
                            hasta {new Date(promotion.end_date).toLocaleDateString()}
                          </div>
                        </div>
                      </TableCell>
                       <TableCell>
                         <Badge variant={isActive(promotion) ? "default" : "secondary"}>
                           {isActive(promotion) ? "Activa" : "Inactiva"}
                         </Badge>
                       </TableCell>
                       <TableCell>
                         <Button
                           variant="outline"
                           size="sm"
                           onClick={() => router.patch(`/admin/promotions/${promotion.id}`, {
                             promotion: { active: !promotion.active }
                           })}
                         >
                           {promotion.active ? 'Desactivar' : 'Activar'}
                         </Button>
                       </TableCell>
                       <TableCell>
                        <div className="flex items-center gap-1">
                          <Percent className="h-4 w-4 text-muted-foreground" />
                          <span>{Number(promotion.average_discount || 0).toFixed(1)}%</span>
                        </div>
                      </TableCell>
                        <TableCell className="text-right">
                         <div className="flex items-center justify-end gap-3">
                           <Button variant="ghost" size="sm" asChild className="hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-all duration-200">
                             <Link href={`/admin/promotions/${promotion.id}`}>
                               <Eye className="h-4 w-4" />
                             </Link>
                           </Button>
                           <Button variant="ghost" size="sm" asChild className="hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-all duration-200">
                             <Link href={`/admin/promotions/${promotion.id}/edit`}>
                               <Edit className="h-4 w-4" />
                             </Link>
                           </Button>
                           <Button
                             variant="ghost"
                             size="sm"
                             asChild
                             className="hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-all duration-200"
                           >
                             <Link
                               href={`/admin/promotions/${promotion.id}`}
                               method="delete"
                               as="button"
                               data-confirm="¿Estás seguro de que quieres eliminar esta promoción?"
                             >
                               <Trash2 className="h-4 w-4" />
                             </Link>
                           </Button>
                         </div>
                       </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default Index;
